'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export const BackgroundRippleEffect = ({
  cellSize = 56,
  borderColor = 'rgba(255,255,255,0.30)',
  fillColor = 'rgba(255,255,255,0.06)',
  align = 'center',
}: {
  cellSize?: number;
  borderColor?: string;
  fillColor?: string;
  align?: 'center' | 'left';
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ rows: 10, cols: 28 });
  const [clickedCell, setClickedCell] = useState<{ row: number; col: number } | null>(null);
  const [rippleKey, setRippleKey] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      setDims({
        rows: Math.ceil(el.offsetHeight / cellSize) + 1,
        cols: Math.ceil(el.offsetWidth / cellSize) + 1,
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cellSize]);

  return (
    <div ref={containerRef} className="absolute inset-0 h-full w-full overflow-hidden">
      <div className="pointer-events-none absolute inset-0 z-[2] h-full w-full" />
      <DivGrid
        key={`base-${rippleKey}`}
        className="opacity-50"
        style={{
          maskImage: align === 'left'
            ? 'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 40%, black 80%)'
            : 'radial-gradient(ellipse at center, rgba(0,0,0,0.2) 20%, black 70%)',
          WebkitMaskImage: align === 'left'
            ? 'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 40%, black 80%)'
            : 'radial-gradient(ellipse at center, rgba(0,0,0,0.2) 20%, black 70%)',
        }}
        rows={dims.rows}
        cols={dims.cols}
        cellSize={cellSize}
        borderColor={borderColor}
        fillColor={fillColor}
        clickedCell={clickedCell}
        onCellClick={(row, col) => {
          setClickedCell({ row, col });
          setRippleKey((k) => k + 1);
        }}
        interactive
      />
    </div>
  );
};

type CellStyle = React.CSSProperties & {
  '--delay'?: string;
  '--duration'?: string;
};

type DivGridProps = {
  className?: string;
  style?: React.CSSProperties;
  rows: number;
  cols: number;
  cellSize: number;
  borderColor: string;
  fillColor: string;
  clickedCell: { row: number; col: number } | null;
  onCellClick?: (row: number, col: number) => void;
  interactive?: boolean;
};

const DivGrid = ({
  className,
  style,
  rows = 7,
  cols = 30,
  cellSize = 56,
  borderColor = 'rgba(255,255,255,0.30)',
  fillColor = 'rgba(255,255,255,0.06)',
  clickedCell = null,
  onCellClick = () => {},
  interactive = true,
}: DivGridProps) => {
  const cells = useMemo(
    () => Array.from({ length: rows * cols }, (_, idx) => idx),
    [rows, cols],
  );

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
    gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
    width: cols * cellSize,
    height: rows * cellSize,
    ...style,
  };

  return (
    <div className={cn('relative z-[3]', className)} style={gridStyle}>
      {cells.map((idx) => {
        const rowIdx = Math.floor(idx / cols);
        const colIdx = idx % cols;
        const distance = clickedCell
          ? Math.hypot(clickedCell.row - rowIdx, clickedCell.col - colIdx)
          : 0;
        const delay = clickedCell ? Math.max(0, distance * 55) : 0;
        const duration = 200 + distance * 80;

        const cellStyle: CellStyle = clickedCell
          ? { '--delay': `${delay}ms`, '--duration': `${duration}ms` }
          : {};

        return (
          <div
            key={idx}
            className={cn(
              'cell relative border-[0.5px] opacity-40 transition-opacity duration-150 will-change-transform hover:opacity-80',
              clickedCell && 'animate-cell-ripple',
              !interactive && 'pointer-events-none',
            )}
            style={{
              backgroundColor: fillColor,
              borderColor: borderColor,
              ...cellStyle,
            }}
            onClick={interactive ? () => onCellClick?.(rowIdx, colIdx) : undefined}
          />
        );
      })}
    </div>
  );
};
